import { isWalletAuthoritativeSnapshot, isWalletCommandResult, } from "@econ-v1/ports";
export const DEFAULT_REPOSITORY_OPERATION_TIMEOUTS_MS = {
    "repository.revisioned.agent.apply": 30_000,
    "repository.revisioned.agent.command": 30_000,
    "repository.revisioned.agent.list": 30_000,
    "repository.revisioned.agent.read": 30_000,
    "repository.revisioned.graph.apply": 30_000,
    "repository.revisioned.graph.command": 30_000,
    "repository.revisioned.graph.list": 30_000,
    "repository.revisioned.graph.read": 30_000,
    "repository.revisioned.settings.apply": 30_000,
    "repository.revisioned.settings.command": 30_000,
    "repository.revisioned.settings.list": 30_000,
    "repository.revisioned.settings.read": 30_000,
    "repository.conversations.acknowledge-message": 30_000,
    "repository.conversations.append-pending-message": 30_000,
    "repository.conversations.apply-delta": 30_000,
    "repository.conversations.fail-message": 30_000,
    "repository.conversations.list-conversations": 30_000,
    "repository.conversations.list-messages": 30_000,
    "repository.events.list-undelivered": 30_000,
    "repository.events.mark-delivered": 30_000,
    "repository.events.mark-delivery-attempt": 30_000,
    "repository.outbox.create": 30_000,
    "repository.outbox.claim-head": 30_000,
    "repository.outbox.delete": 30_000,
    "repository.outbox.list": 30_000,
    "repository.outbox.list-status": 30_000,
    "repository.outbox.read": 30_000,
    "repository.outbox.settle": 30_000,
    "repository.outbox.update-state": 30_000,
    "repository.settings.delete": 30_000,
    "repository.settings.list": 30_000,
    "repository.settings.read": 30_000,
    "repository.settings.set": 30_000,
    "repository.sync-ledger.delete": 30_000,
    "repository.sync-ledger.list": 30_000,
    "repository.sync-ledger.read": 30_000,
    "repository.sync-ledger.set-cursor": 30_000,
    "repository.notifications.apply-delta": 30_000,
    "repository.notifications.list-notifications": 30_000,
    "repository.notifications.mark-read": 30_000,
    "repository.wallet.read-command-result": 30_000,
    "repository.wallet.read-snapshot": 30_000,
    "repository.wallet.write-command-result": 30_000,
    "repository.wallet.write-snapshot": 30_000,
};
export function isRepositoryWorkerOperation(value) {
    return typeof value === "string" && Object.hasOwn(DEFAULT_REPOSITORY_OPERATION_TIMEOUTS_MS, value);
}
export function parseRepositoryWorkerRequest(value) {
    if (!isPositiveInteger(value.id))
        return undefined;
    const id = value.id;
    switch (value.operation) {
        case "repository.revisioned.agent.apply":
        case "repository.revisioned.graph.apply":
        case "repository.revisioned.settings.apply": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["entity"]))
                return undefined;
            const entity = projectRevisionedEntity(value.payload.entity);
            return entity === undefined ? undefined : { id, operation: value.operation, payload: { entity } };
        }
        case "repository.revisioned.agent.command":
        case "repository.revisioned.graph.command":
        case "repository.revisioned.settings.command": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectRevisionCommand(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.revisioned.agent.list":
        case "repository.revisioned.graph.list":
        case "repository.revisioned.settings.list":
            return hasExactOwnKeys(value, ["id", "operation"]) ? { id, operation: value.operation } : undefined;
        case "repository.revisioned.agent.read":
        case "repository.revisioned.graph.read":
        case "repository.revisioned.settings.read":
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["id"]) || typeof value.payload.id !== "string")
                return undefined;
            return { id, operation: value.operation, payload: { id: value.payload.id } };
        case "repository.conversations.acknowledge-message": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectAcknowledgeMessageInput(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.conversations.append-pending-message": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]) || !isRecord(value.payload.input) ||
                !hasExactOwnKeys(value.payload.input, ["message"]))
                return undefined;
            const message = projectConversationMessage(value.payload.input.message);
            return message === undefined ? undefined : { id, operation: value.operation, payload: { input: { message } } };
        }
        case "repository.conversations.apply-delta":
        case "repository.notifications.apply-delta": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["delta"]))
                return undefined;
            const delta = projectConversationLogDelta(value.payload.delta);
            return delta === undefined ? undefined : { id, operation: value.operation, payload: { delta } };
        }
        case "repository.conversations.fail-message": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectFailMessageInput(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.conversations.list-conversations": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["params"]))
                return undefined;
            const params = projectConversationListParams(value.payload.params);
            return params === undefined ? undefined : { id, operation: value.operation, payload: { params } };
        }
        case "repository.conversations.list-messages": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["conversationId", "params"]) ||
                typeof value.payload.conversationId !== "string")
                return undefined;
            const params = projectMessageListParams(value.payload.params);
            return params === undefined ? undefined : {
                id,
                operation: value.operation,
                payload: { conversationId: value.payload.conversationId, params },
            };
        }
        case "repository.events.list-undelivered":
        case "repository.settings.list":
        case "repository.sync-ledger.list":
            return hasExactOwnKeys(value, ["id", "operation"])
                ? { id, operation: value.operation }
                : undefined;
        case "repository.events.mark-delivered": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["deliveredAt", "sequence"]))
                return undefined;
            return isPositiveInteger(value.payload.sequence) && isTimestamp(value.payload.deliveredAt)
                ? { id, operation: value.operation, payload: {
                        deliveredAt: value.payload.deliveredAt,
                        sequence: value.payload.sequence,
                    } }
                : undefined;
        }
        case "repository.events.mark-delivery-attempt": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["sequence"]))
                return undefined;
            return isPositiveInteger(value.payload.sequence)
                ? { id, operation: value.operation, payload: { sequence: value.payload.sequence } }
                : undefined;
        }
        case "repository.outbox.create": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectOutboxRecord(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.outbox.claim-head": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["domain", "leaseExpiresAt", "leaseToken"]) ||
                typeof value.payload.domain !== "string" || typeof value.payload.leaseToken !== "string" ||
                !isTimestamp(value.payload.leaseExpiresAt))
                return undefined;
            return {
                id,
                operation: value.operation,
                payload: {
                    domain: value.payload.domain,
                    leaseExpiresAt: value.payload.leaseExpiresAt,
                    leaseToken: value.payload.leaseToken,
                },
            };
        }
        case "repository.outbox.delete":
        case "repository.outbox.read": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["id"]) || typeof value.payload.id !== "string") {
                return undefined;
            }
            return { id, operation: value.operation, payload: { id: value.payload.id } };
        }
        case "repository.outbox.list": {
            if (!requestHasPayload(value))
                return undefined;
            const filter = projectOutboxFilter(value.payload);
            return filter === undefined ? undefined : { id, operation: value.operation, payload: filter };
        }
        case "repository.outbox.list-status": {
            if (!requestHasPayload(value))
                return undefined;
            const filter = projectOutboxFilter(value.payload);
            return filter === undefined ? undefined : { id, operation: value.operation, payload: filter };
        }
        case "repository.outbox.update-state": {
            if (!requestHasPayload(value) ||
                !hasExactOwnKeys(value.payload, ["id", "state"]) ||
                typeof value.payload.id !== "string" ||
                !isOutboxState(value.payload.state))
                return undefined;
            return { id, operation: value.operation, payload: { id: value.payload.id, state: value.payload.state } };
        }
        case "repository.outbox.settle": {
            if (!requestHasPayload(value))
                return undefined;
            const payload = projectOutboxSettleInput(value.payload);
            return payload === undefined ? undefined : { id, operation: value.operation, payload };
        }
        case "repository.settings.delete":
        case "repository.settings.read": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["key"]) || typeof value.payload.key !== "string") {
                return undefined;
            }
            return { id, operation: value.operation, payload: { key: value.payload.key } };
        }
        case "repository.settings.set": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectSettingInput(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.sync-ledger.delete":
        case "repository.sync-ledger.read": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["domain"]) || typeof value.payload.domain !== "string") {
                return undefined;
            }
            return { id, operation: value.operation, payload: { domain: value.payload.domain } };
        }
        case "repository.sync-ledger.set-cursor": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectSyncLedgerInput(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.notifications.list-notifications": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["params"]))
                return undefined;
            const params = projectNotificationListParams(value.payload.params);
            return params === undefined ? undefined : { id, operation: value.operation, payload: { params } };
        }
        case "repository.notifications.mark-read": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["input"]))
                return undefined;
            const input = projectMarkNotificationReadInput(value.payload.input);
            return input === undefined ? undefined : { id, operation: value.operation, payload: { input } };
        }
        case "repository.wallet.read-command-result":
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["idempotencyKey"]) || typeof value.payload.idempotencyKey !== "string")
                return undefined;
            return { id, operation: value.operation, payload: { idempotencyKey: value.payload.idempotencyKey } };
        case "repository.wallet.read-snapshot":
            return hasExactOwnKeys(value, ["id", "operation"]) ? { id, operation: value.operation } : undefined;
        case "repository.wallet.write-command-result": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["idempotencyKey", "result"]) || typeof value.payload.idempotencyKey !== "string")
                return undefined;
            const result = projectWalletCommandResult(value.payload.result);
            return result === undefined ? undefined : { id, operation: value.operation, payload: { idempotencyKey: value.payload.idempotencyKey, result } };
        }
        case "repository.wallet.write-snapshot": {
            if (!requestHasPayload(value) || !hasExactOwnKeys(value.payload, ["snapshot"]))
                return undefined;
            const snapshot = projectWalletSnapshot(value.payload.snapshot);
            return snapshot === undefined ? undefined : { id, operation: value.operation, payload: { snapshot } };
        }
        default:
            return undefined;
    }
}
export async function executeRepositoryWorkerRequest(repositories, value) {
    switch (value.operation) {
        case "repository.revisioned.agent.apply":
            if (repositories.agents === undefined)
                throw new Error("Agent repository is unavailable");
            return await repositories.agents.apply(value.payload.entity);
        case "repository.revisioned.agent.command":
            if (repositories.agents === undefined)
                throw new Error("Agent repository is unavailable");
            return await repositories.agents.command(value.payload.input);
        case "repository.revisioned.agent.list":
            if (repositories.agents === undefined)
                throw new Error("Agent repository is unavailable");
            return await repositories.agents.list();
        case "repository.revisioned.agent.read":
            if (repositories.agents === undefined)
                throw new Error("Agent repository is unavailable");
            return (await repositories.agents.read(value.payload.id)) ?? null;
        case "repository.revisioned.graph.apply":
            if (repositories.graphs === undefined)
                throw new Error("Graph repository is unavailable");
            return await repositories.graphs.apply(value.payload.entity);
        case "repository.revisioned.graph.command":
            if (repositories.graphs === undefined)
                throw new Error("Graph repository is unavailable");
            return await repositories.graphs.command(value.payload.input);
        case "repository.revisioned.graph.list":
            if (repositories.graphs === undefined)
                throw new Error("Graph repository is unavailable");
            return await repositories.graphs.list();
        case "repository.revisioned.graph.read":
            if (repositories.graphs === undefined)
                throw new Error("Graph repository is unavailable");
            return (await repositories.graphs.read(value.payload.id)) ?? null;
        case "repository.revisioned.settings.apply":
            if (repositories.revisionedSettings === undefined)
                throw new Error("Revisioned settings repository is unavailable");
            return await repositories.revisionedSettings.apply(value.payload.entity);
        case "repository.revisioned.settings.command":
            if (repositories.revisionedSettings === undefined)
                throw new Error("Revisioned settings repository is unavailable");
            return await repositories.revisionedSettings.command(value.payload.input);
        case "repository.revisioned.settings.list":
            if (repositories.revisionedSettings === undefined)
                throw new Error("Revisioned settings repository is unavailable");
            return await repositories.revisionedSettings.list();
        case "repository.revisioned.settings.read":
            if (repositories.revisionedSettings === undefined)
                throw new Error("Revisioned settings repository is unavailable");
            return (await repositories.revisionedSettings.read(value.payload.id)) ?? null;
        case "repository.conversations.acknowledge-message": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.acknowledgeMessage(value.payload.input);
        }
        case "repository.conversations.append-pending-message": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.appendPendingMessage(value.payload.input).commit();
        }
        case "repository.conversations.apply-delta": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.applyDelta(value.payload.delta).commit();
        }
        case "repository.conversations.fail-message": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.failMessage(value.payload.input);
        }
        case "repository.conversations.list-conversations": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.listConversations(value.payload.params);
        }
        case "repository.conversations.list-messages": {
            if (repositories.conversations === undefined)
                throw new Error("Conversation repository is unavailable");
            return await repositories.conversations.listMessages(value.payload.conversationId, value.payload.params);
        }
        case "repository.events.list-undelivered":
            return await repositories.events.listUndelivered();
        case "repository.events.mark-delivered":
            await repositories.events.markDelivered(value.payload.sequence, value.payload.deliveredAt);
            return null;
        case "repository.events.mark-delivery-attempt":
            await repositories.events.markDeliveryAttempt(value.payload.sequence);
            return null;
        case "repository.outbox.create":
            return await repositories.outbox.create(value.payload.input);
        case "repository.outbox.claim-head":
            if (repositories.outbox.claimHead === undefined)
                throw new Error("Outbox replay claim is unavailable");
            return (await repositories.outbox.claimHead(value.payload)) ?? null;
        case "repository.outbox.delete":
            return await repositories.outbox.delete(value.payload.id);
        case "repository.outbox.list":
            return await repositories.outbox.list(value.payload);
        case "repository.outbox.list-status":
            if (repositories.outbox.listStatus === undefined)
                throw new Error("Outbox replay status is unavailable");
            return await repositories.outbox.listStatus(value.payload);
        case "repository.outbox.read":
            return (await repositories.outbox.read(value.payload.id)) ?? null;
        case "repository.outbox.update-state":
            return await repositories.outbox.updateState(value.payload.id, value.payload.state);
        case "repository.outbox.settle":
            if (repositories.outbox.settle === undefined)
                throw new Error("Outbox replay settlement is unavailable");
            return await repositories.outbox.settle(value.payload);
        case "repository.settings.delete":
            return await repositories.settings.delete(value.payload.key);
        case "repository.settings.list":
            return await repositories.settings.list();
        case "repository.settings.read":
            return (await repositories.settings.read(value.payload.key)) ?? null;
        case "repository.settings.set":
            return await repositories.settings.set(value.payload.input);
        case "repository.sync-ledger.delete":
            return await repositories.syncLedger.delete(value.payload.domain);
        case "repository.sync-ledger.list":
            return await repositories.syncLedger.list();
        case "repository.sync-ledger.read":
            return (await repositories.syncLedger.read(value.payload.domain)) ?? null;
        case "repository.sync-ledger.set-cursor":
            return await repositories.syncLedger.setCursor(value.payload.input);
        case "repository.notifications.apply-delta": {
            if (repositories.notifications === undefined)
                throw new Error("Notification repository is unavailable");
            return await repositories.notifications.applyDelta(value.payload.delta).commit();
        }
        case "repository.notifications.list-notifications": {
            if (repositories.notifications === undefined)
                throw new Error("Notification repository is unavailable");
            return await repositories.notifications.listNotifications(value.payload.params);
        }
        case "repository.notifications.mark-read": {
            if (repositories.notifications === undefined)
                throw new Error("Notification repository is unavailable");
            return await repositories.notifications.markRead(value.payload.input).commit();
        }
        case "repository.wallet.read-command-result": {
            if (repositories.wallet === undefined)
                throw new Error("Wallet repository is unavailable");
            return (await repositories.wallet.readCommandResult(value.payload.idempotencyKey)) ?? null;
        }
        case "repository.wallet.read-snapshot": {
            if (repositories.wallet === undefined)
                throw new Error("Wallet repository is unavailable");
            return (await repositories.wallet.readSnapshot()) ?? null;
        }
        case "repository.wallet.write-command-result": {
            if (repositories.wallet === undefined)
                throw new Error("Wallet repository is unavailable");
            await repositories.wallet.writeCommandResult(value.payload.idempotencyKey, value.payload.result);
            return null;
        }
        case "repository.wallet.write-snapshot": {
            if (repositories.wallet === undefined)
                throw new Error("Wallet repository is unavailable");
            await repositories.wallet.writeSnapshot(value.payload.snapshot);
            return null;
        }
    }
}
export function projectRepositoryWorkerResponse(operation, value) {
    switch (operation) {
        case "repository.revisioned.agent.apply":
        case "repository.revisioned.graph.apply":
        case "repository.revisioned.settings.apply":
            return projectWriteResult(value, projectRevisionedRepositoryEvent);
        case "repository.revisioned.agent.command":
        case "repository.revisioned.graph.command":
        case "repository.revisioned.settings.command":
            return projectRevisionCommandResult(value);
        case "repository.revisioned.agent.list":
        case "repository.revisioned.graph.list":
        case "repository.revisioned.settings.list":
            return projectArray(value, projectRevisionedEntity);
        case "repository.revisioned.agent.read":
        case "repository.revisioned.graph.read":
        case "repository.revisioned.settings.read":
            return projectOptionalRecord(value, projectRevisionedEntity);
        case "repository.conversations.acknowledge-message":
        case "repository.conversations.append-pending-message":
        case "repository.conversations.fail-message":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "message", ["created", "updated"]));
        case "repository.conversations.apply-delta":
        case "repository.notifications.apply-delta":
            return projectWriteResult(value, projectConversationRepositoryEvent);
        case "repository.conversations.list-conversations":
            return projectArray(value, projectConversation);
        case "repository.conversations.list-messages":
            return projectArray(value, projectConversationMessage);
        case "repository.events.list-undelivered":
            return projectArray(value, projectDurableRepositoryEvent);
        case "repository.events.mark-delivered":
        case "repository.events.mark-delivery-attempt":
            return (value === null ? null : undefined);
        case "repository.outbox.create":
        case "repository.outbox.update-state":
            return projectWriteResult(value, projectOutboxStateChangedEvent);
        case "repository.outbox.claim-head":
            return projectOptionalRecord(value, projectOutboxReplayClaim);
        case "repository.outbox.delete":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "outbox-intent", ["deleted"]));
        case "repository.outbox.list":
            return projectArray(value, projectOutboxRecord);
        case "repository.outbox.list-status":
            return projectArray(value, projectOutboxReplayStatus);
        case "repository.outbox.read":
            return projectOptionalRecord(value, projectOutboxRecord);
        case "repository.outbox.settle":
            return projectWriteResult(value, projectOutboxStateChangedEvent);
        case "repository.settings.delete":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "setting", ["deleted"]));
        case "repository.settings.set":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "setting", ["created", "updated"]));
        case "repository.settings.list":
            return projectArray(value, projectSettingRecord);
        case "repository.settings.read":
            return projectOptionalRecord(value, projectSettingRecord);
        case "repository.sync-ledger.delete":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "sync-cursor", ["deleted"]));
        case "repository.sync-ledger.list":
            return projectArray(value, projectSyncLedgerRecord);
        case "repository.sync-ledger.read":
            return projectOptionalRecord(value, projectSyncLedgerRecord);
        case "repository.sync-ledger.set-cursor":
            return projectWriteResult(value, projectSyncDeltaAppliedEvent);
        case "repository.notifications.list-notifications":
            return projectArray(value, projectNotification);
        case "repository.notifications.mark-read":
            return projectWriteResult(value, (event) => projectEntityChangedEvent(event, "notification", ["created", "updated"]));
        case "repository.wallet.read-command-result":
            return projectOptionalRecord(value, projectWalletCommandResult);
        case "repository.wallet.read-snapshot":
            return projectOptionalRecord(value, projectWalletSnapshot);
        case "repository.wallet.write-command-result":
        case "repository.wallet.write-snapshot":
            return (value === null ? null : undefined);
    }
}
function projectWriteResult(value, projectEvent) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["eventSequences", "events"]))
        return undefined;
    if (!isExactArray(value.eventSequences) || !value.eventSequences.every(isPositiveInteger))
        return undefined;
    const events = projectArray(value.events, projectEvent);
    if (events === undefined || events.length !== value.eventSequences.length)
        return undefined;
    return { eventSequences: [...value.eventSequences], events };
}
function projectDurableRepositoryEvent(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["deliveredAt", "deliveryAttempts", "event", "sequence"])) {
        return undefined;
    }
    const event = projectStorageRepositoryEvent(value.event);
    if (event === undefined ||
        !isPositiveInteger(value.sequence) ||
        !isNonNegativeInteger(value.deliveryAttempts) ||
        value.deliveredAt !== undefined)
        return undefined;
    return {
        deliveredAt: value.deliveredAt,
        deliveryAttempts: value.deliveryAttempts,
        event,
        sequence: value.sequence,
    };
}
function projectStorageRepositoryEvent(value) {
    if (!isRecord(value) || typeof value.type !== "string")
        return undefined;
    switch (value.type) {
        case "domain.entity-changed": {
            if (!isRecord(value.payload))
                return undefined;
            switch (value.payload.entityType) {
                case "conversation":
                    return projectEntityChangedEvent(value, "conversation", ["created", "updated"]);
                case "message":
                    return projectEntityChangedEvent(value, "message", ["created", "updated"]);
                case "notification":
                    return projectEntityChangedEvent(value, "notification", ["created", "updated"]);
                case "outbox-intent":
                    return projectEntityChangedEvent(value, "outbox-intent", ["deleted"]);
                case "setting":
                    return projectEntityChangedEvent(value, "setting", ["created", "deleted", "updated"]);
                case "sync-cursor":
                    return projectEntityChangedEvent(value, "sync-cursor", ["deleted"]);
                case "agent":
                    return projectEntityChangedEvent(value, "agent", ["created", "updated"]);
                case "graph":
                    return projectEntityChangedEvent(value, "graph", ["created", "updated"]);
                default:
                    return undefined;
            }
        }
        case "outbox.state-changed":
            return projectOutboxStateChangedEvent(value);
        case "sync.delta-applied":
            return projectSyncDeltaAppliedEvent(value);
        default:
            return undefined;
    }
}
function projectEntityChangedEvent(value, expectedEntityType, expectedActions) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["occurredAt", "payload", "type"]) ||
        value.type !== "domain.entity-changed" ||
        !isTimestamp(value.occurredAt) ||
        !isRecord(value.payload) ||
        !hasExactOwnKeys(value.payload, ["action", "entityId", "entityType"]) ||
        !isEntityAction(value.payload.action) ||
        typeof value.payload.entityId !== "string" ||
        typeof value.payload.entityType !== "string" ||
        (expectedEntityType !== undefined && value.payload.entityType !== expectedEntityType) ||
        (expectedActions !== undefined && !expectedActions.includes(value.payload.action)))
        return undefined;
    return {
        occurredAt: value.occurredAt,
        payload: {
            action: value.payload.action,
            entityId: value.payload.entityId,
            entityType: value.payload.entityType,
        },
        type: "domain.entity-changed",
    };
}
function projectRevisionedRepositoryEvent(value) {
    const event = projectEntityChangedEvent(value);
    if (event === undefined || event.type !== "domain.entity-changed" ||
        (event.payload.entityType !== "agent" && event.payload.entityType !== "graph" && event.payload.entityType !== "setting")) {
        return undefined;
    }
    return event;
}
function projectRevisionedEntity(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["id", "rev", "value"]) ||
        typeof value.id !== "string" || value.id.trim() === "" || !isNonNegativeInteger(value.rev))
        return undefined;
    const payload = projectJsonValue(value.value);
    return payload === undefined ? undefined : { id: value.id, rev: value.rev, value: payload };
}
function projectRevisionCommand(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["baseRev", "id", "idempotencyKey", "value"]) ||
        typeof value.id !== "string" || value.id.trim() === "" || typeof value.idempotencyKey !== "string" ||
        value.idempotencyKey.trim() === "" || !isNonNegativeInteger(value.baseRev))
        return undefined;
    const payload = projectJsonValue(value.value);
    return payload === undefined ? undefined : {
        baseRev: value.baseRev,
        id: value.id,
        idempotencyKey: value.idempotencyKey,
        value: payload,
    };
}
function projectRevisionCommandResult(value) {
    if (!isRecord(value) || typeof value.status !== "string")
        return undefined;
    if (value.status === "accepted" && hasExactOwnKeys(value, ["entity", "status"])) {
        const entity = projectRevisionedEntity(value.entity);
        return entity === undefined ? undefined : { entity, status: "accepted" };
    }
    if (value.status === "conflict" && hasExactOwnKeys(value, ["status", "theirs"])) {
        const theirs = projectRevisionedEntity(value.theirs);
        return theirs === undefined ? undefined : { status: "conflict", theirs };
    }
    return undefined;
}
function projectOutboxStateChangedEvent(value) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["occurredAt", "payload", "type"]) ||
        value.type !== "outbox.state-changed" ||
        !isTimestamp(value.occurredAt) ||
        !isRecord(value.payload) ||
        !hasExactOwnKeys(value.payload, ["intent"]))
        return undefined;
    const intent = projectOutboxRecord(value.payload.intent);
    return intent === undefined ? undefined : {
        occurredAt: value.occurredAt,
        payload: { intent },
        type: "outbox.state-changed",
    };
}
function projectSyncDeltaAppliedEvent(value) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["occurredAt", "payload", "type"]) ||
        value.type !== "sync.delta-applied" ||
        !isTimestamp(value.occurredAt))
        return undefined;
    const payload = projectSyncLedgerInput(value.payload);
    return payload === undefined ? undefined : {
        occurredAt: value.occurredAt,
        payload,
        type: "sync.delta-applied",
    };
}
function projectOutboxRecord(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, [
        "createdAt",
        "domain",
        "id",
        "idempotencyKey",
        "payload",
        "state",
        "updatedAt",
    ]))
        return undefined;
    const payload = projectJsonValue(value.payload);
    if (payload === undefined ||
        !isTimestamp(value.createdAt) ||
        typeof value.domain !== "string" ||
        typeof value.id !== "string" ||
        typeof value.idempotencyKey !== "string" ||
        !isOutboxState(value.state) ||
        !isTimestamp(value.updatedAt))
        return undefined;
    return {
        createdAt: value.createdAt,
        domain: value.domain,
        id: value.id,
        idempotencyKey: value.idempotencyKey,
        payload,
        state: value.state,
        updatedAt: value.updatedAt,
    };
}
function projectOutboxReplayClaim(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, [
        "attemptCount", "createdAt", "domain", "id", "idempotencyKey", "leaseExpiresAt", "leaseToken",
        "payload", "state", "updatedAt",
    ]))
        return undefined;
    const payload = projectJsonValue(value.payload);
    if (payload === undefined || !isNonNegativeInteger(value.attemptCount) ||
        !isTimestamp(value.createdAt) || typeof value.domain !== "string" || typeof value.id !== "string" ||
        typeof value.idempotencyKey !== "string" || !isTimestamp(value.leaseExpiresAt) ||
        typeof value.leaseToken !== "string" || value.state !== "replaying" || !isTimestamp(value.updatedAt)) {
        return undefined;
    }
    return {
        attemptCount: value.attemptCount,
        createdAt: value.createdAt,
        domain: value.domain,
        id: value.id,
        idempotencyKey: value.idempotencyKey,
        leaseExpiresAt: value.leaseExpiresAt,
        leaseToken: value.leaseToken,
        payload,
        state: "replaying",
        updatedAt: value.updatedAt,
    };
}
function projectOutboxReplayStatus(value) {
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.domain !== "string" ||
        !isTimestamp(value.createdAt) || !isTimestamp(value.updatedAt) || !isNonNegativeInteger(value.attemptCount) ||
        !isOutboxState(value.state))
        return undefined;
    const optionalFields = ["lastErrorCode", "lastErrorMessage", "nextAttemptAt", "settledAt", "terminalErrorCode", "terminalErrorMessage"];
    const ownOptionalFields = optionalFields.filter((field) => Object.hasOwn(value, field));
    if (!hasExactOwnKeys(value, ["attemptCount", "createdAt", "domain", "id", "state", "updatedAt", ...ownOptionalFields])) {
        return undefined;
    }
    for (const field of ["nextAttemptAt", "settledAt"]) {
        if (value[field] !== undefined && value[field] !== null && !isTimestamp(value[field]))
            return undefined;
    }
    for (const field of ["lastErrorCode", "lastErrorMessage", "terminalErrorCode", "terminalErrorMessage"]) {
        if (value[field] !== undefined && value[field] !== null && typeof value[field] !== "string")
            return undefined;
    }
    return {
        attemptCount: value.attemptCount,
        createdAt: value.createdAt,
        domain: value.domain,
        id: value.id,
        ...(typeof value.lastErrorCode === "string" ? { lastErrorCode: value.lastErrorCode } : {}),
        ...(typeof value.lastErrorMessage === "string" ? { lastErrorMessage: value.lastErrorMessage } : {}),
        ...(typeof value.nextAttemptAt === "string" ? { nextAttemptAt: value.nextAttemptAt } : {}),
        ...(typeof value.settledAt === "string" ? { settledAt: value.settledAt } : {}),
        state: value.state,
        ...(typeof value.terminalErrorCode === "string" ? { terminalErrorCode: value.terminalErrorCode } : {}),
        ...(typeof value.terminalErrorMessage === "string" ? { terminalErrorMessage: value.terminalErrorMessage } : {}),
        updatedAt: value.updatedAt,
    };
}
function projectOutboxSettleInput(value) {
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.leaseToken !== "string" ||
        typeof value.outcome !== "string")
        return undefined;
    if (value.outcome === "acknowledged" && hasExactOwnKeys(value, ["id", "leaseToken", "outcome"])) {
        return { id: value.id, leaseToken: value.leaseToken, outcome: "acknowledged" };
    }
    if (!isRecord(value.error) || !hasExactOwnKeys(value.error, ["code", "message"]) ||
        typeof value.error.code !== "string" || typeof value.error.message !== "string")
        return undefined;
    const error = { code: value.error.code, message: value.error.message };
    if ((value.outcome === "failed" || value.outcome === "conflict") &&
        hasExactOwnKeys(value, ["error", "id", "leaseToken", "outcome"])) {
        return { error, id: value.id, leaseToken: value.leaseToken, outcome: value.outcome };
    }
    if (value.outcome === "retry" && hasExactOwnKeys(value, ["error", "id", "leaseToken", "outcome", "retryAt"]) &&
        isTimestamp(value.retryAt)) {
        return { error, id: value.id, leaseToken: value.leaseToken, outcome: "retry", retryAt: value.retryAt };
    }
    return undefined;
}
function projectOutboxFilter(value) {
    if (!isRecord(value))
        return undefined;
    const keys = [
        ...(Object.hasOwn(value, "domain") ? ["domain"] : []),
        ...(Object.hasOwn(value, "state") ? ["state"] : []),
    ];
    if (!hasExactOwnKeys(value, keys) ||
        (Object.hasOwn(value, "domain") && typeof value.domain !== "string") ||
        (Object.hasOwn(value, "state") && !isOutboxState(value.state)))
        return undefined;
    return {
        ...(typeof value.domain === "string" ? { domain: value.domain } : {}),
        ...(isOutboxState(value.state) ? { state: value.state } : {}),
    };
}
function projectAcknowledgeMessageInput(value) {
    if (!isRecord(value))
        return undefined;
    const optionalKeys = optionalKeysOf(value, ["serverMessageId"]);
    if (!hasExactOwnKeys(value, ["acknowledgedAt", "messageId", ...optionalKeys]) ||
        !isTimestamp(value.acknowledgedAt) || typeof value.messageId !== "string" ||
        (value.serverMessageId !== undefined && typeof value.serverMessageId !== "string"))
        return undefined;
    return {
        acknowledgedAt: value.acknowledgedAt,
        messageId: value.messageId,
        ...(typeof value.serverMessageId === "string" ? { serverMessageId: value.serverMessageId } : {}),
    };
}
function projectFailMessageInput(value) {
    return isRecord(value) && hasExactOwnKeys(value, ["failedAt", "messageId", "reason"]) &&
        isTimestamp(value.failedAt) && typeof value.messageId === "string" && typeof value.reason === "string"
        ? { failedAt: value.failedAt, messageId: value.messageId, reason: value.reason }
        : undefined;
}
function projectMarkNotificationReadInput(value) {
    return isRecord(value) && hasExactOwnKeys(value, ["notificationId", "readAt"]) &&
        typeof value.notificationId === "string" && isTimestamp(value.readAt)
        ? { notificationId: value.notificationId, readAt: value.readAt }
        : undefined;
}
function projectConversationListParams(value) {
    return projectPageParams(value);
}
function projectMessageListParams(value) {
    return projectPageParams(value);
}
function projectNotificationListParams(value) {
    if (!isRecord(value))
        return undefined;
    const keys = [
        ...(Object.hasOwn(value, "limit") ? ["limit"] : []),
        ...(Object.hasOwn(value, "unreadOnly") ? ["unreadOnly"] : []),
    ];
    if (!hasExactOwnKeys(value, keys) ||
        (Object.hasOwn(value, "limit") && !isPageLimit(value.limit)) ||
        (Object.hasOwn(value, "unreadOnly") && typeof value.unreadOnly !== "boolean"))
        return undefined;
    return {
        ...(isPageLimit(value.limit) ? { limit: value.limit } : {}),
        ...(typeof value.unreadOnly === "boolean" ? { unreadOnly: value.unreadOnly } : {}),
    };
}
function projectPageParams(value) {
    if (!isRecord(value))
        return undefined;
    const keys = [
        ...(Object.hasOwn(value, "before") ? ["before"] : []),
        ...(Object.hasOwn(value, "limit") ? ["limit"] : []),
    ];
    if (!hasExactOwnKeys(value, keys) ||
        (Object.hasOwn(value, "before") && typeof value.before !== "string") ||
        (Object.hasOwn(value, "limit") && !isPageLimit(value.limit)))
        return undefined;
    return {
        ...(typeof value.before === "string" ? { before: value.before } : {}),
        ...(isPageLimit(value.limit) ? { limit: value.limit } : {}),
    };
}
function projectConversationLogDelta(value) {
    if (!isRecord(value) || typeof value.kind !== "string")
        return undefined;
    switch (value.kind) {
        case "conversation.upsert": {
            if (!hasExactOwnKeys(value, ["conversation", "kind"]))
                return undefined;
            const conversation = projectConversation(value.conversation);
            return conversation === undefined ? undefined : { conversation, kind: value.kind };
        }
        case "message.append": {
            if (!hasExactOwnKeys(value, ["kind", "message"]))
                return undefined;
            const message = projectConversationMessage(value.message);
            return message === undefined ? undefined : { kind: value.kind, message };
        }
        case "message.ack": {
            const optionalKeys = optionalKeysOf(value, ["serverMessageId"]);
            if (!hasExactOwnKeys(value, ["acknowledgedAt", "kind", "messageId", ...optionalKeys]) ||
                !isTimestamp(value.acknowledgedAt) || typeof value.messageId !== "string" ||
                (value.serverMessageId !== undefined && typeof value.serverMessageId !== "string"))
                return undefined;
            return {
                acknowledgedAt: value.acknowledgedAt,
                kind: value.kind,
                messageId: value.messageId,
                ...(typeof value.serverMessageId === "string" ? { serverMessageId: value.serverMessageId } : {}),
            };
        }
        case "message.fail":
            return hasExactOwnKeys(value, ["failedAt", "kind", "messageId", "reason"]) &&
                isTimestamp(value.failedAt) && typeof value.messageId === "string" && typeof value.reason === "string"
                ? { failedAt: value.failedAt, kind: value.kind, messageId: value.messageId, reason: value.reason }
                : undefined;
        case "notification.upsert": {
            if (!hasExactOwnKeys(value, ["kind", "notification"]))
                return undefined;
            const notification = projectNotification(value.notification);
            return notification === undefined ? undefined : { kind: value.kind, notification };
        }
        case "notification.read":
            return hasExactOwnKeys(value, ["kind", "notificationId", "readAt"]) &&
                typeof value.notificationId === "string" && isTimestamp(value.readAt)
                ? { kind: value.kind, notificationId: value.notificationId, readAt: value.readAt }
                : undefined;
        default:
            return undefined;
    }
}
function projectConversationRepositoryEvent(value) {
    const event = projectEntityChangedEvent(value);
    if (event === undefined || event.type !== "domain.entity-changed" ||
        (event.payload.entityType !== "conversation" && event.payload.entityType !== "message" &&
            event.payload.entityType !== "notification"))
        return undefined;
    return event;
}
function projectConversation(value) {
    if (!isRecord(value))
        return undefined;
    const optionalKeys = optionalKeysOf(value, ["lastMessageAt", "lastMessagePreview"]);
    if (!hasExactOwnKeys(value, ["id", "participantIds", "title", "unreadCount", "updatedAt", ...optionalKeys]) ||
        typeof value.id !== "string" || typeof value.title !== "string" || !isNonNegativeInteger(value.unreadCount) ||
        !isTimestamp(value.updatedAt) || !isStringArray(value.participantIds) ||
        (value.lastMessageAt !== undefined && !isTimestamp(value.lastMessageAt)) ||
        (value.lastMessagePreview !== undefined && typeof value.lastMessagePreview !== "string"))
        return undefined;
    return {
        id: value.id,
        ...(typeof value.lastMessagePreview === "string" ? { lastMessagePreview: value.lastMessagePreview } : {}),
        ...(typeof value.lastMessageAt === "string" ? { lastMessageAt: value.lastMessageAt } : {}),
        participantIds: value.participantIds,
        title: value.title,
        unreadCount: value.unreadCount,
        updatedAt: value.updatedAt,
    };
}
function projectConversationMessage(value) {
    if (!isRecord(value))
        return undefined;
    const optionalKeys = optionalKeysOf(value, ["clientMessageId", "failureReason", "serverMessageId"]);
    if (!hasExactOwnKeys(value, ["body", "conversationId", "createdAt", "id", "senderId", "state", ...optionalKeys]) ||
        typeof value.body !== "string" || typeof value.conversationId !== "string" || !isTimestamp(value.createdAt) ||
        typeof value.id !== "string" || typeof value.senderId !== "string" || !isConversationMessageState(value.state) ||
        (value.clientMessageId !== undefined && typeof value.clientMessageId !== "string") ||
        (value.failureReason !== undefined && typeof value.failureReason !== "string") ||
        (value.serverMessageId !== undefined && typeof value.serverMessageId !== "string"))
        return undefined;
    return {
        body: value.body,
        ...(typeof value.clientMessageId === "string" ? { clientMessageId: value.clientMessageId } : {}),
        conversationId: value.conversationId,
        createdAt: value.createdAt,
        ...(typeof value.failureReason === "string" ? { failureReason: value.failureReason } : {}),
        id: value.id,
        ...(typeof value.serverMessageId === "string" ? { serverMessageId: value.serverMessageId } : {}),
        senderId: value.senderId,
        state: value.state,
    };
}
function projectNotification(value) {
    if (!isRecord(value))
        return undefined;
    const optionalKeys = optionalKeysOf(value, ["readAt", "referenceId"]);
    if (!hasExactOwnKeys(value, ["body", "createdAt", "id", "title", "type", ...optionalKeys]) ||
        typeof value.body !== "string" || !isTimestamp(value.createdAt) || typeof value.id !== "string" ||
        typeof value.title !== "string" || typeof value.type !== "string" ||
        (value.readAt !== undefined && !isTimestamp(value.readAt)) ||
        (value.referenceId !== undefined && typeof value.referenceId !== "string"))
        return undefined;
    return {
        body: value.body,
        createdAt: value.createdAt,
        id: value.id,
        ...(typeof value.readAt === "string" ? { readAt: value.readAt } : {}),
        ...(typeof value.referenceId === "string" ? { referenceId: value.referenceId } : {}),
        title: value.title,
        type: value.type,
    };
}
function optionalKeysOf(value, keys) {
    return keys.filter((key) => Object.hasOwn(value, key));
}
function isPageLimit(value) {
    return isPositiveInteger(value) && value <= 100;
}
function isStringArray(value) {
    return isExactArray(value) && value.every((entry) => typeof entry === "string");
}
function isConversationMessageState(value) {
    return value === "pending" || value === "confirmed" || value === "failed";
}
function projectSettingInput(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["key", "value"]) || typeof value.key !== "string") {
        return undefined;
    }
    const settingValue = projectJsonValue(value.value);
    return settingValue === undefined ? undefined : { key: value.key, value: settingValue };
}
function projectSettingRecord(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["key", "updatedAt", "value"]))
        return undefined;
    const settingValue = projectJsonValue(value.value);
    return settingValue === undefined || typeof value.key !== "string" || !isTimestamp(value.updatedAt)
        ? undefined
        : { key: value.key, updatedAt: value.updatedAt, value: settingValue };
}
function projectSyncLedgerInput(value) {
    if (!isRecord(value) ||
        !hasExactOwnKeys(value, ["cursor", "domain"]) ||
        typeof value.cursor !== "string" ||
        typeof value.domain !== "string")
        return undefined;
    return { cursor: value.cursor, domain: value.domain };
}
function projectSyncLedgerRecord(value) {
    if (!isRecord(value) || !hasExactOwnKeys(value, ["cursor", "domain", "updatedAt"]))
        return undefined;
    return typeof value.cursor !== "string" || typeof value.domain !== "string" || !isTimestamp(value.updatedAt)
        ? undefined
        : { cursor: value.cursor, domain: value.domain, updatedAt: value.updatedAt };
}
function projectJsonValue(value, ancestors = new WeakSet()) {
    if (value === null || typeof value === "boolean" || typeof value === "string")
        return value;
    if (typeof value === "number")
        return Number.isFinite(value) ? value : undefined;
    const array = isExactArray(value);
    if (!array && !isRecord(value))
        return undefined;
    if (ancestors.has(value))
        return undefined;
    ancestors.add(value);
    try {
        if (array) {
            const projected = value.map((entry) => projectJsonValue(entry, ancestors));
            return projected.every((entry) => entry !== undefined) ? projected : undefined;
        }
        if (!Reflect.ownKeys(value).every((key) => typeof key === "string"))
            return undefined;
        const entries = [];
        for (const [key, entry] of Object.entries(value)) {
            const projected = projectJsonValue(entry, ancestors);
            if (projected === undefined)
                return undefined;
            entries.push([key, projected]);
        }
        return Object.fromEntries(entries);
    }
    finally {
        ancestors.delete(value);
    }
}
function projectArray(value, project) {
    if (!isExactArray(value))
        return undefined;
    const projected = value.map(project);
    return projected.every((entry) => entry !== undefined) ? projected : undefined;
}
function projectWalletSnapshot(value) {
    return isWalletAuthoritativeSnapshot(value) ? value : undefined;
}
function projectWalletCommandResult(value) {
    return isWalletCommandResult(value) ? value : undefined;
}
function isExactArray(value) {
    if (!Array.isArray(value) || Reflect.ownKeys(value).length !== value.length + 1)
        return false;
    for (let index = 0; index < value.length; index += 1) {
        if (!Object.hasOwn(value, index))
            return false;
    }
    return true;
}
function projectOptionalRecord(value, project) {
    return value === null ? null : project(value);
}
function requestHasPayload(value) {
    return hasExactOwnKeys(value, ["id", "operation", "payload"]) && isRecord(value.payload);
}
function isRecord(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function hasExactOwnKeys(value, expected) {
    const keys = Reflect.ownKeys(value);
    return keys.length === expected.length && expected.every((key) => Object.hasOwn(value, key));
}
function isTimestamp(value) {
    if (typeof value !== "string")
        return false;
    try {
        return new Date(value).toISOString() === value;
    }
    catch {
        return false;
    }
}
function isPositiveInteger(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}
function isNonNegativeInteger(value) {
    return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
function isOutboxState(value) {
    return value === "acknowledged" || value === "conflict" || value === "failed" || value === "pending" || value === "replaying";
}
function isEntityAction(value) {
    return value === "created" || value === "deleted" || value === "updated";
}
//# sourceMappingURL=storage-repository-protocol.js.map